import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { mediaAPI } from '@/lib/api';
import { BrutalCard, BrutalButton } from '@/components/brutal';
import { Lock, Play, Pause, Compass } from 'lucide-react';
import { toast } from 'sonner';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';

// Every approved song, as this child sees it: playable ones play, locked ones say what to do about it.
const MusicDiscover = ({ student }) => {
  const player = useMusicPlayer();
  const { data: songs = [] } = useQuery({
    queryKey: ['student-media-catalog', student?.id],
    queryFn: async () => (await mediaAPI.getCatalog(student.id)).data,
    enabled: !!student?.id,
  });
  if (songs.length === 0) return null;

  const asTrack = (s) => ({ ...s, media_id: s.id });
  const open = songs.filter((s) => !s.locked);
  const locked = songs.length - open.length;

  return (
    <div className="space-y-3" data-testid="music-discover">
      <h3 className="text-xl font-black uppercase flex items-center gap-2"><Compass size={20} /> Discover music ({songs.length})</h3>
      {locked > 0 && <p className="text-sm text-gray-500">{locked} {locked === 1 ? 'song is' : 'songs are'} locked. Tap a lock to see how to unlock it.</p>}
      <div className="grid gap-3">
        {songs.map((s) => {
          const current = player.currentTrack?.media_id === s.id;
          const playing = current && player.isPlaying;
          return (
            <BrutalCard key={s.id} shadow="sm" data-testid={`discover-${s.id}`}>
              <div className="flex items-center gap-3">
                <button
                  className={`w-12 h-12 flex items-center justify-center border-4 border-black flex-shrink-0 ${s.locked ? 'bg-amber-100' : playing ? 'bg-indigo-200' : 'bg-indigo-100'}`}
                  onClick={() => s.locked ? toast.info(s.lock_message) : (current ? player.togglePlay() : player.playTrack(asTrack(s)))}
                  aria-label={s.locked ? `${s.title} is locked` : `Play ${s.title}`}
                >
                  {s.locked ? <Lock size={22} className="text-amber-600" /> : playing ? <Pause size={22} className="text-indigo-600" /> : <Play size={22} className="text-indigo-600" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-black truncate">{s.title}</p>
                  <p className="text-xs text-gray-500 truncate">{[s.artist, s.country, s.genre].filter(Boolean).join(' · ')}</p>
                  {s.locked && <p className="text-xs font-bold text-amber-700 mt-0.5" data-testid={`discover-note-${s.id}`}>{s.lock_message}</p>}
                </div>
                {s.locked && (
                  <BrutalButton size="sm" onClick={() => toast.info(s.lock_message)}>Why?</BrutalButton>
                )}
              </div>
            </BrutalCard>
          );
        })}
      </div>
    </div>
  );
};

export default MusicDiscover;
